import { useEffect, useState } from 'react'

import {
  useLazyGetTagNamesFromIdsQuery,
  useLazyTagsQuery,
} from '../../../../store/api'
import type {
  MetaResponse,
  PaginatedQueryParams,
} from '../../../../store/types'
import type { Tag } from '../../../../store/types/tag'
import type { DropdownOption } from '../../types'
import AsyncPaginatedDropdown from '../async-paginate'
import CreatebleAsyncPaginatedDropdown from '../createable-async-paginate'
import SeparatableAsyncPaginateDropdown from '../separator-async-paginate'

type TagsDropdownProps = {
  name?: string
  onChange?: (items: DropdownOption[]) => void
  onChangeForManageTable?: (item: DropdownOption) => void
  onSubmit?: () => void
  isClearable?: boolean
  value?: DropdownOption[]
  isCreatable?: boolean
  isMulti?: boolean
  customComponent?: boolean
  separator?: string
  setSeparator?: (value: string | undefined) => void
  styles?: any
  components?: any
  filterValues?: string
}

const tagsToDropdownOptions = (tag: Tag): DropdownOption => ({
  label: tag.name,
  value: tag.id.toString(),
})

export default function TagsDropdown({
  name,
  onChange,
  onChangeForManageTable,
  onSubmit,
  value,
  isClearable = false,
  isCreatable = true,
  isMulti = false,
  customComponent = false,
  separator,
  setSeparator,
  styles,
  components,
  filterValues,
}: TagsDropdownProps) {
  const [fetcher] = useLazyTagsQuery()
  const tagsFetcher = async (params: PaginatedQueryParams) => {
    const res = await fetcher({
      ...params,
      orderBy: 'name',
      orderDirection: 'asc',
    })
    const response: { data?: DropdownOption[]; meta?: MetaResponse<Tag> } = {}

    response.data = res.data?.data.map(tagsToDropdownOptions)
    response.meta = res.data?.meta

    return response
  }
  const [defaultTagValues, setDefaultTagValues] = useState<
    Array<{ label: string; value: string }>
  >([])

  const [getTagNames, { isLoading }] = useLazyGetTagNamesFromIdsQuery()

  useEffect(() => {
    if (filterValues) {
      getTagNames({ ids: filterValues })
        .then((response) => {
          const items = response?.data.map((item: any) => ({
            label: item?.name,
            value: item?.id.toString(),
            isNegative: item?.is_negative,
          }))
          const idList = filterValues
            .split(',')
            .map((item: string) => Math.abs(Number(item)).toFixed())
          items.sort(
            (i1: any, i2: any) =>
              idList.indexOf(i1.value) - idList.indexOf(i2.value),
          )

          setDefaultTagValues(items)
        })
        .catch((err) => {})
    } else {
      setDefaultTagValues([])
    }
  }, [filterValues])

  let Element = AsyncPaginatedDropdown
  if (isCreatable) {
    Element = CreatebleAsyncPaginatedDropdown
  }
  if (customComponent) {
    return (
      <SeparatableAsyncPaginateDropdown
        isReady={!isLoading}
        fetcher={tagsFetcher}
        defaultValue={defaultTagValues}
        onChange={onChange}
        onSubmit={onSubmit}
        isClearable={isClearable}
        isMulti={isMulti}
        value={value}
        customComponent={customComponent}
        separator={separator}
        setSeparator={setSeparator}
        canBeNegative={true}
      />
    )
  } else
    return (
      <Element
        placeholder={
          isCreatable
            ? 'Type to create or select a tag…'
            : 'Type to select a tag…'
        }
        key={defaultTagValues[0]?.value}
        fetcher={tagsFetcher}
        name={name}
        defaultValue={defaultTagValues}
        onChange={onChangeForManageTable ?? onChange}
        isClearable={isClearable}
        isMulti={isMulti}
        value={value}
        customComponent={customComponent}
        separator={separator}
        setSeparator={setSeparator}
        styles={styles}
        components={components}
      />
    )
}
